import React, { useState, useEffect, useRef } from "react";
import html2pdf from "html2pdf.js";
import { fetchWrapper } from "utils/apiFetch";

/**
 * PayoutReceiptModal
 *
 * Receipt for a wallet payout transaction, styled the same way as
 * AEPSReceiptModa. Field mapping matches the response shape of
 * GET https://pro.mokshiq.in/show_payout_receipt?id={id}
 *
 * {
 *   data: {
 *     created_at, aadhar_number, txn_nu, account_number, holder_name,
 *     product, status, customer, amount, order, charge,
 *     wallet_balance, bank_rrn, user
 *   },
 *   mtypea
 * }
 *
 * Usage:
 *   // A) You already have the receipt data (e.g. from a table row / list API)
 *   <PayoutReceiptModal isOpen={open} onClose={close} receiptData={row} />
 *
 *   // B) You only have the id — the modal fetches it itself on open
 *   <PayoutReceiptModal isOpen={open} onClose={close} payoutId={37960} />
 */
export function PayoutReceiptModal({
  receiptData = null,
  payoutId = null,
  isOpen = false,
  onClose = () => {},
  title = "Payout Receipt",
  logoUrl = "/assets/images/logo/skpe.png",
  apiUrl = "https://pro.mokshiq.in/show_payout_receipt",
}) {
  const receiptRef = useRef();
  const [fetchedData, setFetchedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const safe = (v, def = "N/A") =>
    v === null || v === undefined || v === "" ? def : v;

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "" || amount === "null") {
      return "₹0.00";
    }
    const n = parseFloat(amount);
    return Number.isNaN(n) ? "₹0.00" : `₹${n.toFixed(2)}`;
  };

  const getStatusClass = (status) => {
    if (status === true || status === "success" || status === "Success" || status === "ACCEPTED" || status === "accepted") return "text-success";
    if (status === false || status === "failed" || status === "Failed") return "text-danger";
    if ( status === "pending" || status === "Pending") return "text-warning";
    return "text-warning";
  };

  const isSuccess = (status) =>
    status === true || status === "success" || status === "Success" || status === "ACCEPTED" || status === "accepted" || status === "pending" || status === "Pending";

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: "Payout Receipt",
          text: "Here is your payout receipt",
          url: window.location.href,
        })
        .catch(() => {});
    } else alert("Sharing not supported");
  };

  const handlePrint = () => {
    if (!receiptRef.current) return;
    const printContents = receiptRef.current.innerHTML;
    const newWindow = window.open("", "_blank");
    newWindow.document.write(`
      <html>
        <head>
          <title>Payout Receipt</title>
          <link rel="stylesheet"
            href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"/>
        </head>
        <body>${printContents}</body>
      </html>
    `);
    newWindow.document.close();
    newWindow.print();
  };

  const handleDownload = () => {
    if (!receiptRef.current) return;
    const opt = {
      margin: 0.5,
      filename: `payout-receipt-${safe(data.order, "receipt")}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };
    html2pdf().set(opt).from(receiptRef.current).save();
  };

  // If only an id was given, fetch the receipt data ourselves when the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    if (receiptData) return; // caller already supplied data
    if (!payoutId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchWrapper(`${apiUrl}?id=${payoutId}`)
     
      .then((json) => {
        if (cancelled) return;
        setFetchedData(json?.data || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Failed to load receipt");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, payoutId, receiptData, apiUrl]);

  if (!isOpen) return null;

  const data = receiptData || fetchedData || {};

  // ✅ PAYOUT FIELD MAPPING (show_payout_receipt API)
  const tableData = [
    { label: "Order ID", value: safe(data.order) },
    { label: "Transaction No.", value: safe(data.txn_nu) },
    { label: "Product", value: safe(data.product) },
    { label: "Customer", value: safe(data.customer) },
    { label: "Account Number", value: safe(data.account_number) },
    { label: "Account Holder", value: safe(data.holder_name) },
    ...(data.aadhar_number ? [{ label: "Aadhar", value: safe(data.aadhar_number) }] : []),
    { label: "Amount", value: formatCurrency(data.amount) },
    { label: "Charge", value: formatCurrency(data.charge) },
    { label: "Wallet Balance", value: formatCurrency(data.wallet_balance) },
    { label: "Bank RRN", value: safe(data.bank_rrn) },
    { label: "Date & Time", value: safe(data.created_at) },
    { label: "Description", value: data.remark },
    {
      label: "Status",
      value: isSuccess(data.status) ? "SUCCESS" : "FAILED",
      isStatus: true,
    },
  ];

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        ref={receiptRef}
        className="modal-dialog modal-fullscreen-md-down"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="main-title">{title}</h4>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body p-0">
            {loading && (
              <div className="d-flex justify-content-center align-items-center p-5">
                <div className="spinner-border text-primary" role="status" />
              </div>
            )}

            {!loading && error && (
              <div className="alert alert-danger m-3" role="alert">
                {error}
              </div>
            )}

            {!loading && !error && (
              <div className="container invoice-container">
                <div className="row">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-body">
                        <div className="table-responsive">
                          <table className="table">
                            {/* HEADER */}
                            <tr>
                              <td>
                                <table className="table table-lg w-100 invoice-header">
                                  <tbody>
                                    <tr>
                                      <td>
                                        <img
                                          src={logoUrl}
                                          alt="LOGO"
                                          style={{ width: "200px", height: "60px", objectFit: "contain" }}
                                        />
                                        <address>Customer Copy - Payout Receipt</address>
                                      </td>

                                      <td>
                                        <div className="text-end">
                                          <h3 className="text-primary">RECEIPT</h3>
                                          <p><strong>{safe(data.created_at)}</strong></p>
                                          <p>
                                            <strong className={getStatusClass(data.status)}>
                                              {isSuccess(data.status) ? "SUCCESS" : "FAILED"}
                                            </strong>
                                          </p>
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </td>
                            </tr>

                            {/* MAIN TABLE */}
                            <tr>
                              <td>
                                <table className="table table-lg table-bottom-border w-100">
                                  <thead>
                                    <tr>
                                      <th>Description</th>
                                      <th>Details</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {tableData.map((row, i) => (
                                      <tr key={i}>
                                        <td style={{ fontWeight: "500" }}>{row.label}</td>
                                        <td>
                                          {row.isStatus ? (
                                            <span className={getStatusClass(data.status)}>
                                              <strong>{row.value}</strong>
                                            </span>
                                          ) : (
                                            row.value
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>

                            {/* FOOTER NOTE */}
                            <tr>
                              <td>
                                <div className="text-center p-3 bg-light rounded">
                                  <strong>Note:</strong> This is a system generated receipt.
                                </div>
                              </td>
                            </tr>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* FOOTER BUTTONS */}
                    <div className="invoice-footer float-end mb-3">
                      <button className="btn btn-primary m-1" onClick={handlePrint}>
                        Print
                      </button>
                      <button className="btn btn-secondary m-1" onClick={handleShare}>
                        Share
                      </button>
                      <button className="btn btn-success m-1" onClick={handleDownload}>
                        Download
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PayoutReceiptModal;